import { Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import { BbDashboard, bbGetDashboard } from '../../../services/blindBoxService';

function toNumber(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function fmtMoneyCompactUSD(v: string | number | undefined | null): string {
  const n = toNumber(v);
  if (n === null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`.replace('.0M', 'M');
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtPercentDelta(today: number | null, yesterday: number | null): string | null {
  if (today === null || yesterday === null || yesterday === 0) return null;
  const pct = ((today - yesterday) / yesterday) * 100;
  const arrow = pct >= 0 ? '↑' : '↓';
  const sign = pct >= 0 ? '+' : '';
  return `${arrow} ${sign}${pct.toFixed(1)}%`;
}

function fmtRemaining(
  endsAtIso: string | null | undefined,
  t: (key: string) => string,
): string {
  if (!endsAtIso) return '—';
  const end = dayjs(endsAtIso);
  if (!end.isValid()) return '—';
  const now = dayjs();
  const diffMin = end.diff(now, 'minute');
  if (diffMin <= 0) return '—';
  const days = Math.floor(diffMin / (60 * 24));
  const hours = Math.floor((diffMin % (60 * 24)) / 60);
  const minutes = diffMin % 60;
  const dayLabel = (n: number) =>
    n === 1 ? t('module.blindBox.timeUnitDay') : t('module.blindBox.timeUnitDays');
  const hourLabel = (n: number) =>
    n === 1 ? t('module.blindBox.timeUnitHour') : t('module.blindBox.timeUnitHours');
  const minuteLabel = (n: number) =>
    n === 1 ? t('module.blindBox.timeUnitMinute') : t('module.blindBox.timeUnitMinutes');
  if (days > 0) {
    return `${days} ${dayLabel(days)}  ${hours} ${hourLabel(hours)}  ${minutes} ${minuteLabel(minutes)}`;
  }
  if (hours > 0) {
    return `${hours} ${hourLabel(hours)}  ${minutes} ${minuteLabel(minutes)}`;
  }
  return `${minutes} ${minuteLabel(minutes)}`;
}

function StatCard({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5" sx={{ mt: 1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export function SectionOverview({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const [d, setD] = useState<BbDashboard | null>(null);
  useEffect(() => {
    bbGetDashboard()
      .then(setD)
      .catch(() => setD(null));
  }, [refreshKey]);
  if (!d) return <Typography>{t('module.blindBox.loading')}</Typography>;

  const todayOpensN = d.todayBoxOpens ?? null;
  const ydayOpensN = d.yesterdayBoxOpens ?? null;
  const opensDelta = fmtPercentDelta(todayOpensN, ydayOpensN);
  const rewardsToday = fmtMoneyCompactUSD(d.todayRewardsDistributedUSDC);
  const rewardsTotal = fmtMoneyCompactUSD(d.totalRewardsDistributedUSDC);

  const statusRaw = (d.activityStatus || '').toLowerCase();
  const statusLabel =
    statusRaw === 'active'
      ? t('module.blindBox.statusActive')
      : statusRaw === 'upcoming'
        ? t('module.blindBox.statusUpcoming')
        : statusRaw === 'completed'
          ? t('module.blindBox.statusCompleted')
          : t('module.blindBox.statusNone');
  const remaining = fmtRemaining(d.activityEndsAt, t);

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title={t('module.blindBox.todayBoxOpens')}
          value={todayOpensN ?? '—'}
          subtitle={
            opensDelta ? `${opensDelta} ${t('module.blindBox.vsYesterday')}` : undefined
          }
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title={t('module.blindBox.todayRewardsDistributedUSDC')}
          value={rewardsToday}
          subtitle={`${t('module.blindBox.total')}: ${rewardsTotal}`}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title={t('module.blindBox.todayActiveUsers')}
          value={d.todayActiveUsers ?? '—'}
          subtitle={`${t('module.blindBox.totalUsersLabel')}: ${d.totalUsers}`}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title={t('module.blindBox.activityStatus')}
          value={statusLabel}
          subtitle={remaining !== '—' ? `${t('module.blindBox.remaining')}: ${remaining}` : undefined}
        />
      </Grid>
    </Grid>
  );
}
